import React from 'react';
import {useDispatch} from 'react-redux';
import {useLocation, useNavigate} from 'react-router';

import {Modals} from '@edna/components';
import {useExactSelector} from '@edna/hooks';

import {ListLoader, Menu} from 'src/components';
import {CommonLayout} from 'src/components/primitives';
import {getPrivatePaths} from 'src/containers/App/routes';
import {selectIsMenuPinned, toggleMenuPinned} from 'src/containers/App/slice';
import {checkCustomLayout} from 'src/containers/App/utils';
import {useSignOutMutation} from 'src/containers/Auth/api';
import {HelpWidget} from 'src/containers/HelpWidget';
import {
  NOTIFICATIONS_POLLING_INTERVAL,
  Notifications,
  usePollNotificationsQuery,
} from 'src/containers/Notifications';
import {
  ERoles,
  getCurrentUser,
  selectTenantId,
  useGetCurrentUserQuery,
  useGetTenantDataQuery,
} from 'src/containers/User';
import PATHS from 'src/paths';
import {EMetricsId, sendMetrics} from 'src/utils';

import MenuItem from './MenuItem';
import MenuListItem from './MenuListItem';
import {EMenuId, TNavigation, bottomMenu, menuItemMetricsHash, topMenu} from './constants';
import * as S from './style';
import {filterAllowedPaths} from './utils';

type TOpenItemId = string | null;

export type TLayoutSettings = {
  isAuth: boolean;
  isAuthByInvite: boolean;
  isTriggers: boolean;
};

type TProps = {
  settings?: TLayoutSettings;
  children: React.ReactNode;
  allowedPrivatePaths?: string[];
};

type TMenuControllerProps = {
  isPinned: boolean;
  isHovered: boolean;
  togglePinned: TEmptyFunction;
  setHovered: (isHovered: boolean) => void;
  renderTopContent: (isOpen: boolean) => React.ReactNode;
  renderBottomContent: (isOpen: boolean) => React.ReactNode;
  renderLogo?: (isOpen: boolean) => React.ReactNode;
  handleLogoClick?: TEmptyFunction;
};

const usePrivateMenuController = (): TMenuControllerProps => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [openItemId, setOpenItemId] = React.useState<TOpenItemId>(null);
  const [isMenuHovered, setIsMenuHovered] = React.useState(false);
  const isMenuPinned = useExactSelector(selectIsMenuPinned);
  const tenantId = useExactSelector(selectTenantId);

  const {data: user} = useGetCurrentUserQuery();
  const {data: tenantData} = useGetTenantDataQuery(undefined, {
    skip: !user || user.roles.includes(ERoles.ROLE_AGENT),
  });

  usePollNotificationsQuery(undefined, {
    pollingInterval: NOTIFICATIONS_POLLING_INTERVAL,
    skipPollingIfUnfocused: true,
    skip: !user,
  });

  const [signOut] = useSignOutMutation();

  const resellerId = React.useMemo(() => {
    if (!user) {
      return null;
    }

    return user.tenants.find((tenant) => tenant.id === tenantId)?.resellerId;
  }, [user, tenantId]);

  const allowedPrivatePaths = React.useMemo(
    () => (user && resellerId ? getPrivatePaths(user.permissions, user.roles, resellerId) : []),
    [user, resellerId],
  );

  const toggleMenuItem = React.useCallback(
    (id: string) => setOpenItemId((prevId) => (prevId === id ? null : id)),
    [setOpenItemId],
  );

  const handleToggleMenuPinned = React.useCallback(() => {
    dispatch(toggleMenuPinned());
  }, [dispatch]);

  const topMenuAllowedPaths = React.useMemo(
    () => filterAllowedPaths(topMenu, allowedPrivatePaths),
    [allowedPrivatePaths],
  );
  const bottomMenuAllowedPaths = React.useMemo(
    () => filterAllowedPaths(bottomMenu, allowedPrivatePaths),
    [allowedPrivatePaths],
  );

  const handleSignOut = React.useCallback(
    async (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      event.preventDefault();
      await signOut()
        .unwrap()
        .then(() => navigate(PATHS.SIGN_IN, {replace: true}))
        .catch(() => {});
    },
    [signOut],
  );

  const handleLogoClick = React.useCallback(() => {
    if (!tenantData?.selfRegistered) {
      return;
    }

    sendMetrics(EMetricsId.MENU_LOGO_CLICK, tenantData?.tenantId);
  }, [sendMetrics, tenantData]);

  const handleMenuItemClick = React.useCallback(
    (id: EMenuId) => () => {
      const metricsId = menuItemMetricsHash[id];

      if (!tenantData?.selfRegistered || !metricsId) {
        return;
      }

      sendMetrics(metricsId, tenantData?.tenantId);
    },
    [sendMetrics, tenantData],
  );

  const renderMenuItemsList = (data: TNavigation[]) => (menuOpen: boolean) => (
    <>
      {data.map((item) => {
        if (item.list) {
          return (
            <MenuListItem
              key={item.id}
              openItemId={openItemId}
              menuOpen={menuOpen}
              item={item}
              toggleMenuItem={toggleMenuItem}
              onItemClick={(id) => handleMenuItemClick(id)()}
            />
          );
        }

        return (
          <MenuItem
            key={item.id}
            data={item}
            onClick={item.id === EMenuId.SIGN_OUT ? handleSignOut : handleMenuItemClick(item.id)}
            hasNotifications={item.id === EMenuId.NOTIFICATIONS}
            hasDialogs={item.id === EMenuId.DIALOGS}
            menuOpen={menuOpen}
          />
        );
      })}
    </>
  );

  return {
    isHovered: isMenuHovered,
    isPinned: isMenuPinned,
    setHovered: setIsMenuHovered,
    togglePinned: handleToggleMenuPinned,
    renderTopContent: renderMenuItemsList(topMenuAllowedPaths),
    renderBottomContent: renderMenuItemsList(bottomMenuAllowedPaths),
    handleLogoClick,
  };
};

const PrivateLayout = React.memo<TProps>(({children}) => {
  const contentRef = React.useRef<HTMLDivElement>(null);

  const {
    isPinned,
    togglePinned,
    isHovered,
    setHovered,
    renderTopContent,
    renderBottomContent,
    handleLogoClick,
  } = usePrivateMenuController();

  return (
    <CommonLayout>
      <Menu
        pinned={isPinned}
        toggleMenuPinned={togglePinned}
        renderTopContent={renderTopContent}
        renderBottomContent={renderBottomContent}
        hovered={isHovered}
        onHoverChange={setHovered}
        homePage={PATHS.HOME}
        onLogoClick={handleLogoClick}
      />
      <S.Main opened={isPinned || isHovered}>
        <S.MainContent ref={contentRef} opened={isPinned || isHovered}>
          <Notifications contentRef={contentRef} />
          <React.Suspense
            fallback={
              <S.SuspenseLoading>
                <ListLoader />
              </S.SuspenseLoading>
            }
          >
            {children}
          </React.Suspense>
          <HelpWidget />
        </S.MainContent>
      </S.Main>
    </CommonLayout>
  );
});

const Layout = React.memo<TProps>(({children}) => {
  const {pathname} = useLocation();
  const hasCustomLayout = checkCustomLayout(pathname);

  const {data: user} = getCurrentUser.useQueryState();

  if (user && !hasCustomLayout) {
    return (
      <>
        <PrivateLayout>{children}</PrivateLayout>
        <Modals />
      </>
    );
  }

  return (
    <>
      <React.Suspense fallback={null}>{children}</React.Suspense>
      <Modals />
    </>
  );
});

Layout.displayName = 'Layout';

export type {TMenuControllerProps};
export {usePrivateMenuController, EMenuId};

export default Layout;
